import * as THREE from 'three';

/** 一层纸的低分辨率 alpha（用于点按时判断"手指下最上面是哪一层"） */
export type AlphaGrid = { size: number; data: Uint8Array };

type Drawable = CanvasImageSource & { width: number; height: number };

function readAlpha(img: Drawable, size: number): Uint8Array {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d', { willReadFrequently: true })!;
  g.drawImage(img, 0, 0, size, size);
  const rgba = g.getImageData(0, 0, size, size).data;
  const a = new Uint8Array(size * size);
  for (let i = 0; i < a.length; i++) a[i] = rgba[i * 4 + 3];
  return a;
}

export function alphaGrid(img: Drawable, size = 128): AlphaGrid {
  return { size, data: readAlpha(img, size) };
}

/** uv：图片归一化坐标，原点左下（three 约定），返回 0..1 */
export function alphaAt(grid: AlphaGrid, u: number, v: number): number {
  if (u < 0 || v < 0 || u >= 1 || v >= 1) return 0;
  const x = Math.floor(u * grid.size);
  const y = Math.floor((1 - v) * grid.size);
  return grid.data[y * grid.size + x] / 255;
}

/** 可分离盒式模糊，三遍 ≈ 高斯 */
function boxBlur(src: Float32Array<ArrayBuffer>, size: number, r: number): Float32Array<ArrayBuffer> {
  let a = src;
  let b = new Float32Array(a.length);
  const w = 2 * r + 1;
  for (let pass = 0; pass < 3; pass++) {
    for (const horizontal of [true, false]) {
      for (let line = 0; line < size; line++) {
        let acc = 0;
        const at = (i: number) => {
          const j = Math.min(size - 1, Math.max(0, i));
          return horizontal ? a[line * size + j] : a[j * size + line];
        };
        for (let i = -r; i <= r; i++) acc += at(i);
        for (let i = 0; i < size; i++) {
          const v = acc / w;
          if (horizontal) b[line * size + i] = v;
          else b[i * size + line] = v;
          acc += at(i + r + 1) - at(i - r);
        }
      }
      [a, b] = [b, a];
    }
  }
  return a;
}

/**
 * 柔和投影用的模糊 alpha 贴图（单通道放在 alpha 里）。只在加载时算一次（256² × 3 遍 ≈ 几毫秒）。
 * 边界外按 0 处理由着色器负责（ClampToEdge + uv 越界判断）。
 */
export function blurredAlphaTexture(img: Drawable, size = 256, radius = 5): THREE.DataTexture {
  const a = readAlpha(img, size);
  const f = new Float32Array(a.length);
  for (let i = 0; i < a.length; i++) f[i] = a[i] / 255;
  const blurred = boxBlur(f, size, radius);
  const out = new Uint8Array(size * size * 4);
  // 画布行序是自上而下，DataTexture 行 0 在底部：翻转 y
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const o = ((size - 1 - y) * size + x) * 4;
      out[o] = out[o + 1] = out[o + 2] = 255;
      out[o + 3] = Math.round(blurred[y * size + x] * 255);
    }
  }
  const tex = new THREE.DataTexture(out, size, size, THREE.RGBAFormat);
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}
